import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { ModuleRegistry } from '@ag-grid-community/core';
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import '@ag-grid-enterprise/core';
import { LicenseManager } from '@ag-grid-enterprise/core';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  MAT_DIALOG_DEFAULT_OPTIONS,
  MatDialogConfig,
  MatDialogModule
} from '@angular/material/dialog';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EfpApiSdkModule } from '@ats/api-sdk';
import { AppGlobalErrorhandler, EfpAppConfigModule } from '@ats/app-config';
import { EfpComponentMastheadModule } from '@ats/component-masthead';
import { OWL_DATE_TIME_FORMATS, OWL_DATE_TIME_LOCALE } from '@ats/component-ui/date-picker';
import {
  EfactoryBaseService,
  FlagsService,
  OdataService,
  PropertyType,
  ValueTypeTest,
  XmlParser
} from '@ats/service-base';
import { EfactoryPubSubService } from '@ats/service-pubsub';
import { EfactorySiteService } from '@ats/service-site';
import { EfactoryUserInfoService, EfactoryUserService } from '@ats/service-user';
import { EfpServiceUserNotificationModule } from '@ats/service-user-notification';
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { provideTranslocoScope } from '@ngneat/transloco';
import { LOADING_BAR_CONFIG, LoadingBarModule } from '@ngx-loading-bar/core';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { AppComponent } from './app.component';
import { appRouting } from './app.routes';
import { AuthErrorComponent } from './components/auth-error/auth-error.component';
import { ResourceLinkComponent } from './components/resource-link/resource-link.component';
import { NewRelicInterceptor } from './core/interceptors/new-relic.interceptor';
import { TranslocoRootModule } from './transloco-root.module';

// Register AG-Grid modules that are to be available on all grids
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ServerSideRowModelModule,
  MenuModule,
  ColumnsToolPanelModule,
  ClipboardModule
]);

/* eslint-disable max-len */
LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-096369}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{ATS}_is_granted_a_{Multiple_Applications}_Developer_License_for_{3}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{4}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{9_February_2027}____[v3]_[01]_MTgwMjEzMTIwMDAwMA==4b4591a810f0f1e79a145c933e1a9180'
);
/* eslint-enable max-len */

// Global fallback for @ats/component-ui attachment dialog translations.
// Keeps attachment-add labels available even when dialog injector scope resolution is inconsistent.
const attachmentAddLoader = {
  'en-US': () =>
    Promise.resolve({
      'Add Attachment': 'Add Attachment',
      'Edit Attachment': 'Edit Attachment',
      Save: 'Save',
      Cancel: 'Cancel',
      'Attach File': 'Attach File',
      Add: 'Add',
      Attachments: 'Attachments',
      'No attachments added': 'No attachments added',
      Remove: 'Remove',
      'Maximum 10 attachments allowed': 'Maximum 10 attachments allowed',
      button: 'Choose File',
      description: 'Description',
      noFile: 'No file selected',
      'No file selected': 'No file selected',
      'File exceeds maximum size of ~~size~~ MB': 'File exceeds maximum size of {{size}} MB',
      'Invalid File Type': 'Invalid File Type',
      'File size limit ~~size~~ MB': 'File size limit {{size}} MB',
      '(Optional)': '(Optional)'
    }),
  'es-MX': () =>
    Promise.resolve({
      'Add Attachment': 'Agregar Archivo',
      'Edit Attachment': 'Editar Archivo',
      Save: 'Guardar',
      Cancel: 'Cancelar',
      'Attach File': 'Adjuntar Archivo',
      Add: 'Agregar',
      Attachments: 'Archivos adjuntos',
      'No attachments added': 'No se agregaron archivos',
      Remove: 'Quitar',
      'Maximum 10 attachments allowed': 'Máximo de 10 archivos permitidos',
      button: 'Escoger Archivo',
      description: 'Descripción',
      noFile: 'Ningún archivo seleccionado',
      'No file selected': 'Ningún archivo seleccionado',
      'File exceeds maximum size of ~~size~~ MB':
        'El archivo supera el tamaño máximo de {{size}} MB',
      'Invalid File Type': 'Tipo de archivo no permitido',
      'File size limit ~~size~~ MB': 'El tamaño limite por archivo es {{size}} MB',
      '(Optional)': '(Opcional)'
    })
};

@NgModule({
  declarations: [AppComponent, AuthErrorComponent, ResourceLinkComponent],
  imports: [
    appRouting,
    EfpAppConfigModule,
    EfpApiSdkModule,
    // AgGridModule,
    AuthModule.forRoot(),
    // BrowserModule,
    BrowserAnimationsModule,
    // CommonModule,
    // FormsModule,
    // ReactiveFormsModule,
    MatDialogModule,
    // MatInputModule,
    // MatButtonModule,
    // MatFormFieldModule,
    // MatMenuModule,
    // MatNativeDateModule,
    // MatSlideToggleModule,
    // MatRippleModule,
    // MatSelectModule,
    LoadingBarHttpClientModule,
    LoadingBarModule,
    EfpComponentMastheadModule,
    EfpServiceUserNotificationModule,
    // OverlayModule,
    TranslocoRootModule,
    FlexLayoutModule
  ],
  providers: [
    {
      provide: ErrorHandler,
      useClass: AppGlobalErrorhandler
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NewRelicInterceptor,
      multi: true
    },
    EfactoryPubSubService,
    EfactoryBaseService,
    EfactorySiteService,
    EfactoryUserInfoService,
    EfactoryUserService,
    FlagsService,
    OdataService,
    PropertyType,
    ValueTypeTest,
    XmlParser,
    { provide: LOADING_BAR_CONFIG, useValue: { latencyThreshold: 250 } },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        ...new MatDialogConfig(),
        role: 'dialog',
        ariaModal: true,
        panelClass: 'efp-themed-dialog'
      } as MatDialogConfig
    },
    {
      provide: OWL_DATE_TIME_LOCALE,
      useFactory: () => {
        return (navigator.languages ? navigator.languages[0] : navigator.language) as string;
      }
    },
    {
      provide: OWL_DATE_TIME_FORMATS,
      useFactory: () => {
        const lang: string = (
          navigator.languages ? navigator.languages[0] : navigator.language
        ) as string;

        let retVal = {};
        switch (lang.toLowerCase().substring(0, 2)) {
          case 'en':
            retVal = {
              parseInput: null,
              fullPickerInput: {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
              },
              datePickerInput: {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              },
              timePickerInput: {
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
              },
              monthYearLabel: { year: 'numeric', month: 'short' },
              dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
              monthYearA11yLabel: { year: 'numeric', month: 'long' }
            };
            break;
          default:
            retVal = {
              parseInput: null,
              fullPickerInput: {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
              },
              datePickerInput: {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric'
              },
              timePickerInput: {
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
              },
              monthYearLabel: { year: 'numeric', month: 'short' },
              dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
              monthYearA11yLabel: { year: 'numeric', month: 'long' }
            };
        }

        return retVal;
      }
    },
    provideTranslocoScope({
      scope: 'attachment-add',
      alias: 'attachment-add',
      loader: attachmentAddLoader
    })
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
